import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  formatNumber,
  initial,
  mealByTime,
  localNow,
  type HomeSummary,
  type Workout,
  type Gym,
  type Meal,
} from '@jungles/shared';
import { useApi } from '../lib/hooks';
import { useHomeStartup } from '../lib/startup';
import { useUser } from '../app/session';
import {
  ErrorState,
  Header,
  JungleStrip,
  Loader,
  Screen,
  TabBar,
} from '../components/Chrome';
import { ChevronIcon, SparkIcon } from '../components/icons';
import { WorkoutPicker } from '../components/WorkoutPicker';
import { FoodPicker } from '../components/FoodPicker';
import { GymLogo } from '../components/GymLogo';

/** H1–H3 · Главная (SPEC §5). На главной нет списков лиг — только плашка. */

export function HomeScreen() {
  const user = useUser();
  const navigate = useNavigate();
  const { data, error, loading, reload } = useApi<HomeSummary>('/api/home');
  // Reuse the server's owner-scoped, latest-updated saved draft selection.
  const { data: workouts, error: draftError, loading: draftLoading, reload: reloadDraft } =
    useApi<{ draft: Workout | null }>('/api/workouts/summary');
  const resolvedDraft = !draftError && workouts?.draft?.status === 'draft' ? workouts.draft : null;
  const [settledDraft, setSettledDraft] = useState<Workout | null>(null);
  const [pressedDraft, setPressedDraft] = useState<Workout | null>(null);
  const releaseTimer = useRef<ReturnType<typeof setTimeout>>();
  // Background revalidation must not collapse a target already on screen.
  const currentDraft = draftLoading ? settledDraft : resolvedDraft;
  const draft = pressedDraft ?? currentDraft;
  useEffect(() => { if (!draftLoading) setSettledDraft(resolvedDraft); }, [draftLoading, resolvedDraft]);
  useEffect(() => () => clearTimeout(releaseTimer.current), []);
  const releaseDraft = () => {
    clearTimeout(releaseTimer.current);
    // Keep geometry through the compatibility click after pointerup, including
    // an invalidated/deleted draft. The click below validates the latest result.
    releaseTimer.current = setTimeout(() => setPressedDraft(null), 350);
  };
  useEffect(() => {
    const refresh = () => { if (document.visibilityState === 'visible') reloadDraft(); };
    window.addEventListener('focus', refresh);
    document.addEventListener('visibilitychange', refresh);
    return () => {
      window.removeEventListener('focus', refresh);
      document.removeEventListener('visibilitychange', refresh);
    };
  }, [reloadDraft]);
  // homeGymName includes the city for other screens; use the canonical name here.
  const { data: homeGym, loading: gymLoading, error: gymError } = useApi<Gym>(user.homeGymId ? `/api/gyms/${encodeURIComponent(user.homeGymId)}` : null);
  useHomeStartup(loading || draftLoading || gymLoading, !!(error || draftError || gymError), {
    home: !loading && !!data, summary: !draftLoading && !!workouts, gym: !gymLoading,
  });
  const gymName = homeGym?.id === user.homeGymId ? homeGym.name : user.homeGymId ? 'Мой зал' : 'Без зала';
  const [picker, setPicker] = useState<'workout' | 'food' | null>(null);

  if (loading) {
    return (
      <Screen tabs>
        <Header />
        <Loader />
        <TabBar />
      </Screen>
    );
  }

  if (error || !data) {
    return (
      <Screen tabs>
        <Header />
        <ErrorState message={error ?? 'Нет данных'} onRetry={reload} />
        <TabBar />
      </Screen>
    );
  }

  const { week, points, league, allTime, tip } = data;
  const defaultMeal: Meal = mealByTime(localNow(user.timezone).time);

  return (
    <Screen tabs>
      <Header />

      {tip ? (
        <section aria-label="Подсказка jungles AI" style={{ margin: '4px 0 0' }}>
          <div className="ai-ctx">
            <SparkIcon />
            {tip.context}
            <ChevronIcon size={12} />
          </div>
          <p className="ai-h">
            <span className="emo" aria-hidden="true">
              {tip.emoji}
            </span>
            {tip.title}
          </p>
        </section>
      ) : null}

      <div className="quick">
        <button className="qk" type="button" onClick={() => setPicker('workout')}>
          Тренировка
          <span className="pl" aria-hidden="true">
            +
          </span>
          <img src="/assets/ui/tab-workouts.webp" alt="" />
        </button>
        <button className="qk" type="button" onClick={() => setPicker('food')}>
          Питание
          <span className="pl" aria-hidden="true">
            +
          </span>
          <img src="/assets/ui/tab-food.webp" alt="" />
        </button>
      </div>

      {draft ? (
        <Link className="home-unfinished" to={`/workouts/${encodeURIComponent(draft.id)}`}
          onPointerDown={(event) => {
            clearTimeout(releaseTimer.current);
            setPressedDraft(draft);
            event.currentTarget.setPointerCapture(event.pointerId);
          }}
          onPointerUp={releaseDraft}
          onPointerCancel={releaseDraft}
          onLostPointerCapture={releaseDraft}
          onClick={(event) => {
            if (currentDraft?.id !== draft.id) event.preventDefault();
            releaseDraft();
          }}>

          <img src="/assets/ui/tab-workouts.webp" alt="" />
          <span>Завершите тренировку «{draft.title}»</span>
          <ChevronIcon size={16} />
        </Link>
      ) : null}

      <div className="me-flat">
        <span className="me-av" aria-hidden="true">
          {user.photoUrl ? <img src={user.photoUrl} alt="" /> : initial(user.name)}
        </span>
        <div className="nm">
          <b>{user.name.split(' ')[0]}</b>
          <span className="gym-inline">
            {user.homeGymName ? <GymLogo name={user.homeGymName} logoUrl={user.homeGymLogoUrl} size="small" /> : null}
            <span title={gymName}>{gymName}</span>
          </span>
        </div>
        <button
          className="me-pts"
          type="button"
          onClick={() => navigate('/arena')}
          style={{ background: 'none', border: 0, cursor: 'pointer' }}
        >
          <span className="me-n">
            {formatNumber(points.total)}
            <img className="pt-ic" src="/assets/ui/points-diamond-bicep.webp" alt="очков" />
          </span>
          {league ? (
            <span className="me-lg">
              <img src={league.icon} alt="" />
              {league.name} {league.rank ? <b>#{league.rank}</b> : null}
            </span>
          ) : null}
        </button>
      </div>

      <section className="hero" aria-label="Моя неделя">
        <div className="row">
          <h2>Моя неделя</h2>
          <span className="nt">{week.rangeLabel}</span>
        </div>

        <div className="wk">
          {week.days.map((day) => (
            <div
              key={day.date}
              className={`day${day.isToday ? ' today' : ''}${day.isFuture ? ' fut' : ''}`}
            >
              {day.label}
              <b>{day.dayOfMonth}</b>
              <span className="dots">
                <i className={day.hasWorkout ? 'dt t' : 'dt'} />
                <i className={day.hasFood ? 'dt food' : 'dt'} />
              </span>
            </div>
          ))}
        </div>

        <div className="goal">
          <div className="row">
            <Link className="goal-link" to="/workouts"><b>Тренировки</b><ChevronIcon size={14} /></Link>
            <span>
              <b>{week.workoutsDone}</b> из {week.workoutsGoal} в неделю
            </span>
          </div>
          <div
            className="saus t"
            role="img"
            aria-label={`${week.workoutsDone} из ${week.workoutsGoal} тренировок`}
          >
            {Array.from({ length: week.workoutsGoal }, (_, index) => (
              <span key={index} className={index < week.workoutsDone ? 'on' : undefined} />
            ))}
          </div>
        </div>

        <div className="goal">
          <div className="row">
            <Link className="goal-link" to="/nutrition"><b>Питание</b><ChevronIcon size={14} /></Link>
            <span>
              <b>{week.foodDays}</b> из {week.foodDaysGoal} дней
            </span>
          </div>
          <div
            className="saus food"
            role="img"
            aria-label={`${week.foodDays} из ${week.foodDaysGoal} дней с записью питания`}
          >
            {week.days.map((day) => (
              <span key={day.date} className={day.hasFood ? 'on' : undefined} />
            ))}
          </div>
        </div>

        <p className="nt" style={{ margin: '14px 0 0' }}>
          <button
            className="back"
            type="button"
            onClick={() => navigate('/profile')}
            style={{ minHeight: 'auto', padding: 0, textDecoration: 'underline' }}
          >
            Изменить цели
          </button>
        </p>
      </section>

      <div className="secl">За всё время</div>
      <div className="tiles">
        <div className="tile">
          <img src="/assets/ui/elephant.webp" alt="" />
          <small>Поднято</small>
          <b>{formatNumber(allTime.liftedKg)} кг</b>
          <span>{allTime.liftedEquivalent}</span>
        </div>
        <div className="tile">
          <img src="/assets/ui/shark.webp" alt="" />
          <small>Съедено</small>
          <b>{formatNumber(allTime.eatenKcal)} ккал</b>
          <span>{allTime.eatenEquivalent}</span>
        </div>
      </div>

      <JungleStrip />
      <TabBar />

      {picker === 'workout' ? <WorkoutPicker onClose={() => setPicker(null)} /> : null}
      {picker === 'food' ? (
        <FoodPicker meal={defaultMeal} date={data.today} onClose={() => setPicker(null)} />
      ) : null}
    </Screen>
  );
}
